// React
import React from "react";

// MUI
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  ButtonBase,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  styled,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";

import { useTranslation } from "react-i18next";
// Third party imports
import {
  FiChevronDown,
  FiChevronLeft,
  FiCommand,
  FiHelpCircle,
  FiLogOut,
  FiMenu,
  FiSearch,
  FiSettings,
  FiUser,
} from "react-icons/fi";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

// Components
import { loadAiStatus } from "components/AiAssistant/aiStore";
import LanguageSwitcher from "components/LanguageSwitcher";

// Utils
import { BREAKPOINTS, SHELL_NAVBAR_HEIGHT } from "layouts/_shared/shellMetrics";

// Hooks
import useLogout from "hooks/logout/useLogout";
import useGetProfile from "hooks/profile/getProfile";
import { hotkeyToText, openShortcutsHelp } from "hooks/ui/useHotkeys";

import { isClientUser } from "utils/auth.jsx";

import { colors, zIndex } from "styles/tokens";

// Routes
import { PROTECTED_ROUTES } from "../../routes";
import MainBreadcrumbs from "./Breadcrumbs.jsx";

// The AI assistant (mounted by MainLayout) listens to this event to open and close itself
const AI_TOGGLE_EVENT = "ai-assistant:toggle";
const AI_SHORTCUT = "mod+k";

const StyledAppBar = styled(AppBar)({
  backgroundColor: colors.white,
  color: colors.gray900,
  boxShadow: "none",
  // Non-scrolling flex sibling of the page scroller (MainLayout ContentArea)
  position: "relative",
  flexShrink: 0,
  zIndex: zIndex.appBar,
  boxSizing: "border-box",
  height: `var(--shell-navbar-h, ${SHELL_NAVBAR_HEIGHT}px)`,
  borderBottom: `1px solid ${colors.gray200}`,
});
const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  padding: "0 16px",
  height: "100%",
  minHeight: 0,
  [theme.breakpoints.up("sm")]: { minHeight: 0 },
}));
const LeftSection = styled(Box)({
  display: "flex",
  alignItems: "center",
  minWidth: 0,
});
const CenterSection = styled(Box)({
  display: "flex",
  flex: 1,
  alignItems: "center",
  justifyContent: "center",
  minWidth: 0,
});
const RightSection = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  flexShrink: 0,
}));
const AnnouncementsButton = styled(Button)({
  color: colors.gray600,
  minWidth: 0,
  "&:hover": { backgroundColor: colors.gray100, color: colors.gray900 },
});
const UserButton = styled(ButtonBase)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 1, 0.5, 0.5),
  borderRadius: 10,
  color: colors.gray900,
  "&:hover, &[aria-expanded='true']": { backgroundColor: colors.gray100 },
  "&.Mui-focusVisible": { outline: `2px solid ${colors.primary}`, outlineOffset: 2 },
}));

/**
 * Top bar of the CRM: breadcrumbs, the "search or ask the AI" button (opens the assistant, the
 * same as Cmd/Ctrl+K), language, announcements and the user menu. Below 768 (md) the sidebar is an
 * overlay: `onMenuClick` opens it from the hamburger on the left.
 */
const MainNavbar = ({ isSidebarCollapsed = false, onBackClick, onMenuClick }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { handleLogout } = useLogout();
  const { isLoading, profile } = useGetProfile();
  const hasReadLatestAnnouncement = useSelector(
    (state) => state.profile.user?.has_read_latest_announcement
  );

  const [anchorEl, setAnchorEl] = React.useState(null);
  const menuOpen = Boolean(anchorEl);
  const closeMenu = () => setAnchorEl(null);
  // Close first, then act: the item handlers never run with the menu still on screen
  const runFromMenu = (action) => () => {
    closeMenu();
    action();
  };

  // The AI button shows only when the assistant is on (never for client users: loadAiStatus
  // answers false for them without calling the API)
  const [clientUser] = React.useState(isClientUser);
  const [aiEnabled, setAiEnabled] = React.useState(false);
  React.useEffect(() => {
    if (clientUser) return undefined;
    let active = true;
    loadAiStatus().then((on) => {
      if (active) setAiEnabled(on);
    });
    return () => {
      active = false;
    };
  }, [clientUser]);
  const showAiButton = aiEnabled && !clientUser;

  const openAssistant = () => window.dispatchEvent(new CustomEvent(AI_TOGGLE_EVENT));
  const shortcutText = hotkeyToText(AI_SHORTCUT);
  const searchLabel = t("shell:navbar.searchAi");

  const user = profile?.user;
  const fullName = user ? `${user.first_name} ${user.last_name}` : "";

  return (
    <StyledAppBar position="relative">
      <StyledToolbar disableGutters>
        <LeftSection>
          {onMenuClick && (
            <IconButton
              onClick={onMenuClick}
              aria-label={t("shell:sidebar.open")}
              // MUI's own display beats the Tailwind utility layer: hide it from 768 up here
              sx={{
                mr: 1,
                flexShrink: 0,
                display: "inline-flex",
                [`@media (min-width:${BREAKPOINTS.md}px)`]: { display: "none" },
              }}
            >
              <FiMenu />
            </IconButton>
          )}
          {onBackClick && (
            <IconButton
              onClick={onBackClick}
              size="small"
              aria-label={t("common:back")}
              sx={{ ml: isSidebarCollapsed ? 2 : 0 }}
            >
              <FiChevronLeft />
            </IconButton>
          )}
          <MainBreadcrumbs />
        </LeftSection>

        <CenterSection>
          {showAiButton && (
            <button
              type="button"
              onClick={openAssistant}
              aria-label={t("shell:navbar.searchAiAria", { shortcut: shortcutText })}
              className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center gap-2 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-500 transition-colors hover:border-gray-300 hover:bg-white hover:text-gray-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary sm:w-full sm:max-w-md sm:justify-start sm:px-3"
            >
              <FiSearch size={16} aria-hidden="true" className="shrink-0" />
              <span className="hidden min-w-0 flex-1 truncate text-left sm:block">
                {searchLabel}
              </span>
              <kbd className="kbd hidden shrink-0 sm:inline-flex" aria-hidden="true">
                {shortcutText}
              </kbd>
            </button>
          )}
        </CenterSection>

        <RightSection>
          <LanguageSwitcher />

          <Tooltip title={t("common:announcements")}>
            <AnnouncementsButton
              component={Link}
              to={PROTECTED_ROUTES.HELP_CENTER}
              aria-label={t("common:announcements")}
              startIcon={
                <Badge
                  variant="dot"
                  invisible={Boolean(hasReadLatestAnnouncement)}
                  overlap="circular"
                  sx={{ "& .MuiBadge-badge": { backgroundColor: colors.primary } }}
                >
                  <FiHelpCircle size={18} />
                </Badge>
              }
            >
              <span className="hidden lg:inline">{t("common:announcements")}</span>
              {!hasReadLatestAnnouncement && (
                <span className="sr-only">{t("shell:navbar.newAnnouncements")}</span>
              )}
            </AnnouncementsButton>
          </Tooltip>

          {isLoading || !user ? (
            <Box
              role="status"
              aria-label={t("shell:navbar.userMenuLoading")}
              sx={{ display: "flex", alignItems: "center", gap: 1, px: 0.5 }}
            >
              <Skeleton variant="circular" width={32} height={32} animation="wave" />
              <Skeleton
                variant="text"
                width={88}
                height={20}
                animation="wave"
                sx={{ display: { xs: "none", md: "block" } }}
              />
            </Box>
          ) : (
            <Tooltip title={t("common:userProfile")}>
              <UserButton
                onClick={(event) => setAnchorEl(event.currentTarget)}
                aria-label={t("shell:navbar.userMenu")}
                aria-haspopup="menu"
                aria-expanded={menuOpen ? "true" : undefined}
                aria-controls={menuOpen ? "user-menu" : undefined}
              >
                <Avatar
                  alt={fullName}
                  src={user.avatar?.file || "/default-avatar.jpg"}
                  sx={{ width: 32, height: 32 }}
                />
                <Typography
                  component="span"
                  sx={{ display: { xs: "none", md: "block" }, fontWeight: 500, fontSize: 14 }}
                >
                  {`${user.first_name} ${user.last_name?.[0] ?? ""}.`}
                </Typography>
                <FiChevronDown size={14} aria-hidden="true" color={colors.gray500} />
              </UserButton>
            </Tooltip>
          )}
        </RightSection>
      </StyledToolbar>

      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 220, mt: 0.5 } } }}
      >
        {fullName && (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.gray900 }} noWrap>
              {fullName}
            </Typography>
            {user?.email && (
              <Typography sx={{ fontSize: 12, color: colors.gray500 }} noWrap>
                {user.email}
              </Typography>
            )}
          </Box>
        )}
        {fullName && <Divider sx={{ my: 0.5 }} />}
        <MenuItem onClick={runFromMenu(() => navigate(PROTECTED_ROUTES.PROFILE))}>
          <ListItemIcon>
            <FiUser size={18} />
          </ListItemIcon>
          <ListItemText>{t("common:profile")}</ListItemText>
        </MenuItem>
        <MenuItem onClick={runFromMenu(() => navigate(PROTECTED_ROUTES.SETTINGS))}>
          <ListItemIcon>
            <FiSettings size={18} />
          </ListItemIcon>
          <ListItemText>{t("shell:navbar.settings")}</ListItemText>
        </MenuItem>
        <MenuItem onClick={runFromMenu(openShortcutsHelp)}>
          <ListItemIcon>
            <FiCommand size={18} />
          </ListItemIcon>
          <ListItemText>{t("shell:navbar.shortcuts")}</ListItemText>
          <kbd className="kbd ml-4" aria-hidden="true">
            ?
          </kbd>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={runFromMenu(handleLogout)} sx={{ color: colors.danger }}>
          <ListItemIcon sx={{ color: "inherit" }}>
            <FiLogOut size={18} />
          </ListItemIcon>
          <ListItemText>{t("common:logout")}</ListItemText>
        </MenuItem>
      </Menu>
    </StyledAppBar>
  );
};
export default MainNavbar;
